import { router } from "@inertiajs/react"
import { Button } from "@/components/ui/button"
import { ForgeButton } from "@/components/ui/forge-button"
import { Card } from "@/components/ui/card"
import { fmtCompactRelative } from "@/lib/format"
import type {
  EscalationPolicyCapabilities,
  EscalationPolicySet,
  PolicyDiffSummary,
} from "@/types/escalation_policy"

// Top-of-page banner for /escalation_policies. Four render modes covering
// every (live × draft) combination:
//   A) No live, no draft → empty-state CTA. "Auto-triage isn't set up yet"
//      + Add-your-first-rule and Build-with-Forge buttons.
//   B) No live, draft only → first-time-author. "Viewing: Draft" + Forge /
//      Promote / Discard. No live promotion-meta line.
//   C) Live, no draft → quiet single-line summary + gold "Edit policies"
//      that POSTs an empty body (controller auto-clones live → draft).
//   D) Live + draft → full diff banner. Left = current view + change count
//      + switch-view link. Right = Forge / Promote (gated on canPromote) /
//      Discard.
//
// Promote is gated on `policyCapabilities.canPromote` — agent_managers can edit
// the draft but only admins push it live.

type Props = {
  liveSet: EscalationPolicySet | null
  draftSet: EscalationPolicySet | null
  /** The set the page is currently rendering. May be archived (operator
   *  clicked an archived dot in the version timeline). When archived,
   *  banner switches to a read-only mode and hides Promote / Discard. */
  activeSet: EscalationPolicySet
  diff: PolicyDiffSummary
  policyCapabilities: EscalationPolicyCapabilities
  /** "live" or "draft" — driven by the parent's view-toggle state. */
  viewing: "live" | "draft"
  onSwitchView: (next: "live" | "draft") => void
  onOpenDesigner: () => void
}

export function PolicyDiffBanner({
  liveSet, draftSet, activeSet, diff, policyCapabilities, viewing, onSwitchView, onOpenDesigner,
}: Props) {
  const hasLive  = liveSet  !== null
  const hasDraft = draftSet !== null

  // Read-only mode — operator clicked an archived dot. Show a quiet
  // banner with version info + a "Back to live" link. Mutations are
  // already gated on `editable = activeSet.state === "draft"` upstream.
  if (activeSet.state === "archived") {
    return (
      <Card className="px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-0.5 text-sm">
            <div className="text-app-text">
              <span className="rounded-control border border-border bg-white/5 px-2 py-0.5 text-[11px] uppercase tracking-wide text-app-muted">
                Read-only
              </span>{" "}
              <span className="font-medium">Viewing v{activeSet.version} (archived)</span>
              {activeSet.promoted_at && (
                <span className="ml-2 text-xs text-app-subtle">
                  promoted {fmtCompactRelative(activeSet.promoted_at)}
                  {activeSet.promoted_by ? ` by ${activeSet.promoted_by}` : ""}
                </span>
              )}
            </div>
            <div className="text-xs text-app-muted">
              This version is no longer in effect. To make changes, switch back to live and start a new draft.
            </div>
          </div>
          <button
            type="button"
            onClick={() => onSwitchView("live")}
            className="text-xs text-brand-red hover:underline"
          >
            Back to live
          </button>
        </div>
      </Card>
    )
  }

  // State A — nothing exists. Encourage starting from scratch.
  if (!hasLive && !hasDraft) {
    return (
      <Card className="px-4 py-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="space-y-0.5 text-sm">
            <div className="font-medium text-app-text">Auto-triage isn't set up yet.</div>
            <div className="text-xs text-app-muted">
              Add a rule manually or ask Forge to draft a starting set based on your tags + escalation reasons.
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              variant="red"
              size="sm"
              onClick={startDraft}
              disabled={!policyCapabilities.canEdit}
              title={!policyCapabilities.canEdit ? "Editing requires agent_manager or admin" : undefined}
            >
              Add your first rule
            </Button>
            <ForgeButton size="sm" onClick={onOpenDesigner} disabled={!policyCapabilities.canEdit}>
              Build with Forge
            </ForgeButton>
          </div>
        </div>
      </Card>
    )
  }

  // State C — live exists, no draft yet. "Edit with Forge" clones
  // live → draft AND opens the Forge designer in one click so the
  // operator can immediately start drafting the next policy version.
  if (hasLive && !hasDraft) {
    return (
      <Card className="px-4 py-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm text-app-text">
            <span className="font-medium">Viewing:</span>{" "}
            <span className="text-app-muted">
              Live{liveSet?.promoted_at && (
                <> · last promoted {fmtCompactRelative(liveSet.promoted_at)}{liveSet.promoted_by ? ` by ${liveSet.promoted_by}` : ""}</>
              )}
            </span>
          </div>
          <ForgeButton
            size="sm"
            onClick={() => startDraftAndOpenForge(onOpenDesigner)}
            disabled={!policyCapabilities.canEdit}
            title={!policyCapabilities.canEdit ? "Editing requires agent_manager or admin" : "Clone live → draft and open Forge"}
          >
            Edit with Forge
          </ForgeButton>
        </div>
      </Card>
    )
  }

  // Draft exists — full banner.
  const changes = diff.total_changes ?? 0
  const changeText =
    changes === 0 ? "No changes vs live yet."
    : changes === 1 ? "1 change vs live"
                    : `${changes} changes vs live`

  return (
    <Card className="px-4 py-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="space-y-1 text-sm">
          <div className="text-app-text">
            <span className="font-medium">Viewing:</span>{" "}
            <span className={viewing === "draft" ? "text-status-warning" : "text-app-muted"}>
              {viewing === "draft" ? `Draft (${changeText})` : "Live"}
            </span>
          </div>
          {liveSet?.promoted_at && (
            <div className="text-xs text-app-subtle">
              Live promoted {fmtCompactRelative(liveSet.promoted_at)}
              {liveSet.promoted_by ? ` by ${liveSet.promoted_by}` : ""}
            </div>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <ForgeButton size="sm" onClick={onOpenDesigner} disabled={!policyCapabilities.canEdit}>
            Edit with Forge
          </ForgeButton>
          <Button
            variant="red"
            size="sm"
            onClick={promote}
            disabled={!policyCapabilities.canPromote || changes === 0 || viewing === "live"}
            title={
              !policyCapabilities.canPromote ? "Promote requires admin"
              : viewing === "live"     ? "Switch to the draft to promote"
              : changes === 0          ? "No changes to promote"
                                       : "Promote draft to live"
            }
          >
            Promote
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={discardDraft}
            disabled={!policyCapabilities.canEdit || viewing === "live"}
            title={
              !policyCapabilities.canEdit ? "Editing requires agent_manager or admin"
              : viewing === "live"  ? "Switch to the draft to discard it"
                                    : "Discard the current draft"
            }
          >
            Discard draft
          </Button>
        </div>
      </div>
    </Card>
  )
}

// -- handlers --------------------------------------------------------------

function startDraft() {
  // POST /escalation_policies with no body — the controller auto-clones live
  // → draft when none exists. Server response is a redirect-back so Inertia
  // refreshes props with the new draft set.
  router.post("/escalation_policies", {}, { preserveScroll: true })
}

// One-click "Edit with Forge" from the live view: POST to clone live →
// draft (server-side via CloneLiveToDraft), and once Inertia has refreshed
// the props with the new draft set, open the Forge designer pane.
function startDraftAndOpenForge(onOpenDesigner: () => void) {
  router.post("/escalation_policies", {}, {
    preserveScroll: true,
    onSuccess: () => onOpenDesigner(),
  })
}

function promote() {
  if (!confirm("Promote this draft to live? The current live set will be archived.")) return
  router.post("/escalation_policies/promote", {}, { preserveScroll: true })
}

function discardDraft() {
  if (!confirm("Discard the current draft? All unpromoted changes will be lost.")) return
  router.post("/escalation_policies/discard_draft", {}, { preserveScroll: true })
}
